import BaseApi from "./base-api";
import { objToQuerystring } from "../shared/helpers/querystring-helper";
import moment from "moment";

import {
  statusActive,
  perPage,
  fromGhin,
  sortByFullName,
} from "../shared/helpers/api-helper-b";
import { getUserLoginToken } from "../shared/helpers/api-helper-b";

class GolferApi extends BaseApi {
  apiUrl = `${this.baseUrl}/api/v1/golfermethods.asmx`;

  login = (
    password,
    email_or_ghin,
    remember_me = false,
    turnstileToken = null,
    validateTurnstile = true
  ) => {
    const params = {
      user: {
        password,
        email_or_ghin,
        remember_me,
      },
      token: getUserLoginToken(),
      turnstile_token: turnstileToken,
      validate_turnstile: validateTurnstile,
    };

    return this.httpPost(`${this.baseUrl}/api/v1/golfer_login.json`, params);
  };

  logout = () => {
    const url = `${this.baseUrl}/api/v1/users/logout.json`;
    return this.httpDelete(`${url}`);
  };

  getClubs = (golfer_id) => {
    const url = `${this.baseUrl}/api/v1/search_golfer.json`;
    const params = {
      golfer_id: golfer_id,
      ...statusActive,
    };
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  getClubGolfers = (club, filter = null, sortCriteria, page = 1) => {
    const url = `${this.baseUrl}/api/v1/clubs/${club}/golfers.json`;
    const params = {
      ...statusActive,
      ...perPage,
      ...sortCriteria,
      page,
    };
    if (filter && filter.length > 0) {
      if (isNaN(filter)) {
        params.full_name = filter;
      } else {
        params.golfer_id = filter;
      }
    }
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  getAllGolfers = (country, state, extraParams, page = 1) => {
    const url = `${this.baseUrl}/api/v1/golfers.json`;
    let params;
    if (state === "All") {
      params = {
        ...statusActive,
        ...fromGhin,
        ...perPage,
        ...sortByFullName,
        page,
        country,
      };
    } else {
      params = {
        ...statusActive,
        ...fromGhin,
        ...perPage,
        ...sortByFullName,
        page,
        state,
      };
    }
    if (extraParams) {
      params = { ...params, ...extraParams };
    }
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  golferProfile = (payload) => {
    const url = `${this.baseUrl}/api/v1/golfers/search.json`;
    const params = {
      ...payload,
      page: 1,
      per_page: 100,
    };
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  findGolfer = (golfer_id, page = 1) => {
    const url = `${this.baseUrl}/api/v1/golfers.json`;
    const params = {
      ...statusActive,
      ...fromGhin,
      ...perPage,
      page,
      golfer_id,
      includeLowHandicapIndex: true,
    };
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  getHandicapHistory = (ghin_number) => {
    const date_begin = moment().subtract(1, "y").format("YYYY-MM-DD");
    const date_end = moment().format("YYYY-MM-DD");
    const url = `${this.baseUrl}/api/v1/golfers/${ghin_number}/handicap_history.json`;
    const params = {
      revCount: 0,
      date_begin,
      date_end,
    };
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  getHandicapHistoryLowhi = (golfer) => {
    const url = `${this.baseUrl}/api/v1/clubs/${golfer.club_id}/golfers/${golfer.ghin_number}.json`;
    return this.httpGet(url);
  };

  searchGolfer = (golfer_id) => {
    const url = `${this.baseUrl}/api/v1/search_golfer.json`;
    return this.httpGet(`${url}?${objToQuerystring({ golfer_id })}`);
  };

  getGolferAssociations = (golferId) => {
    const url = `${this.baseUrl}/api/v1/golfers/${golferId}/associations.json`;
    return this.httpGet(url);
  };

  disableAutoRenewal = (payload) => {
    const url = `${this.baseUrl}/api/v1/golfers/${payload.golfer_id}/club_affiliations/${payload.affiliation_id}.json`;
    return this.httpPatch(url, payload.data);
  };

  createProfile = (payload) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/ghin_account_verification.json?${objToQuerystring(
        payload
      )}`,
      false
    );
  };

  checkTrial = (golferId, screen) => {
    const url = `${this.baseUrl}/api/v1/golfers/${golferId}/check_trial.json?screen=${screen}`;
    return this.httpGet(url);
  };

  createTrial = (payload) => {
    const params = {
      golfer: {
        first_name: payload.firstName,
        last_name: payload.lastName,
        gender: payload.gender[0],
        email: payload.email,
        password: payload.password,
        password_confirmation: payload.confirmPassword,
        terms_accepted: true,
        zip_code: payload.zipCode,
      },
      turnstile_token: payload.turnstileToken,
    };
    return this.httpPost(
      `${this.baseUrl}/api/v1/golfers/create_trial_golfer.json`,
      params
    );
  };

  resendVerification = (payload) => {
    return this.httpPost(
      `${
        this.baseUrl
      }/api/v1/resend_email_after_ghin_account_verification.json?${objToQuerystring(
        payload
      )}`,
      false
    );
  };

  golferInformation = (payload) => {
    return this.httpGet(
      `${
        this.baseUrl
      }/api/v1/get_golfer_user_information.json?${objToQuerystring(payload)}`,
      false
    );
  };

  setPassword = (payload) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/users/set_password.json?${objToQuerystring(
        payload
      )}`,
      false
    );
  };

  emailChangeInformation = (golfer_id, payload) => {
    const querystring = objToQuerystring(payload);
    return this.httpGet(
      `${this.baseUrl}/api/v1/golfers/${golfer_id}/get_email_change_information.json?${querystring}`,
      false
    );
  };

  confirmEmailChange = (golfer_id, payload) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/golfers/${golfer_id}/confirm_email_change.json`,
      payload
    );
  };

  forgotLogin = (payload) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/forgot_login_information.json?${objToQuerystring(
        payload
      )}`,
      false
    );
  };

  acceptTerms = () => {
    return this.httpPost(`${this.baseUrl}/api/v1/accept_terms.json`);
  };

  searchMinorGolfer = (payload) => {
    const url = `${this.baseUrl}/api/v1/golfers/search.json`;
    const params = {
      page: 1,
      per_page: 10,
      golfer_id: payload,
    };
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  fetchMyProfile = (payload) => {
    const url = `${this.baseUrl}/api/v1/clubs/${payload.club_id}/golfers/${payload.ghin}.json`;
    return this.httpGet(url);
  };

  patchMyProfile = (payload) => {
    const url = `${this.baseUrl}/api/v1/clubs/${payload.club_id}/golfers/${payload.ghin}.json`;
    return this.httpPatch(url, payload.data);
  };

  fetchMinors = (guardian_id) => {
    const url = `${this.baseUrl}/api/v1/guardians/${guardian_id}/golfers.json`;
    return this.httpGet(url);
  };

  loginWithGhin = (password, email_or_ghin) => {
    const params = {
      user: {
        password,
        email_or_ghin,
      },
    };
    return this.httpPost(`${this.baseUrl}/api/v1/login_with_ghin.json`, params);
  };

  authorizeLoginWithGhin = (url, token) => {
    return this.httpGet(url, token);
  };

  preAuthorizeLoginWithGhin = (url, token) => {
    return this.httpPost(url, { commit: "Authorize" }, token);
  };

  denyAuthorize = (url, token) => {
    return this.httpDelete(url, { commit: "Deny" }, token);
  };

  getGuardianData = (gId) => {
    const url = `${this.baseUrl}/api/v1/guardians/${gId}.json`;
    return this.httpGet(url);
  };

  getData = (id, params) => {
    const url = `${this.baseUrl}/api/v1/challenges/${id}/leaderboard.json`;
    return this.httpGet(`${url}?${objToQuerystring(params)}`);
  };

  getLeaderboardToken = () => {
    const url = `${this.baseUrl}/api/v1/leaderboard_subscription.json`;
    return this.httpGet(url);
  };

  getPrivateLeaderboardToken = (page, challenge_id) => {
    const url = `${this.baseUrl}/api/v1/private_leaderboard_subscription.json?page=${page}&challenge_id=${challenge_id}`;
    return this.httpGet(url);
  };

  resetPassword = (payload) => {
    const url = `${this.baseUrl}/api/v1/users/reset_password.json`;
    return this.httpPatch(url, payload);
  };

  joinChallenge = (golferId, challengeId) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/golfers/${golferId}/challenge/${challengeId}/join.json`
    );
  };

  leaveChallenge = (golferId, challengeId) => {
    return this.httpDelete(
      `${this.baseUrl}/api/v1/challenge/${challengeId}/golfers/${golferId}.json`
    );
  };

  challengeDisplayPreference = (golferId, challengeId, displayChallenge) => {
    return this.httpPost(
      `${this.baseUrl}/api/v1/challenges/${challengeId}/golfers/${golferId}/display_preference.json`,
      {
        display_challenge: displayChallenge,
      }
    );
  };

  getChallengeData = (id) => {
    const url = `${this.baseUrl}/api/v1/challenges/${id}.json`;
    return this.httpGet(url);
  };

  getChallengeDetails = (id) => {
    const url = `${this.baseUrl}/api/v1/scores/${id}/challenge.json`;
    return this.httpGet(url);
  };

  getGolferChallengeData = (id) => {
    const url = `${this.baseUrl}/api/v1/golfer/${id}/challenges.json`;
    return this.httpGet(url);
  };

  getLeaderboardUpdateDate = (cId) => {
    const url = `${this.baseUrl}/api/v1/challenge/${cId}/updated_at.json`;
    return this.httpGet(url);
  };
}
export default new GolferApi();
